import React, { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  Text,
  TextProps,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { seriesStop, tokens } from 'containers/Intelligence/theme';
import { scaledPx } from 'containers/Intelligence/theme/scale';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import { formatNumber } from 'utils/formatters';

import type { IChartTooltipTheme } from '../tooltipTheme';

export interface IVerticalColumn {
  label: string;
  /** The column's reading; in grouped mode its total across the series. */
  value: number;
  /** Grouped mode: one reading per `series` entry, in the same order. */
  values?: number[];
}

/** One series of the grouped mode: its name for the readout and its fill. */
export interface IVerticalColumnSeries {
  name: string;
  color: string;
}

/** Axis and grid styling, where a screen draws its own. */
export interface IVerticalColumnAxisStyle {
  tickColor?: string;
  /** Tick caption size in drawn px; it follows the responsive scale. */
  tickFontSize?: number;
  tickFontFamily?: string;
  axisLineColor?: string;
  /** Draws dashed-capable horizontal gridlines in this ink; without it there are none. */
  gridColor?: string;
  gridDasharray?: string;
  /** Inks the highlighted column's caption, in bold. */
  highlightTickColor?: string;
  yAxisWidth?: number;
}

/**
 * While a bar is hovered: a dashed line from the Y axis to its top. With `bubble`, also an 8px dot
 * and the reading in a bubble above it, which then replaces the tooltip - the drawn hover state of
 * PLA-9674's quarterly chart.
 */
export interface IVerticalColumnHoverGuide {
  /** The line's ink; defaults to the hovered bar's own fill. */
  color?: string;
  /** `fontSize` is the reading's size in drawn px; without it the reading takes the tick size. */
  bubble?: { fill: string; text: string; fontSize?: number };
}

export interface IVerticalColumnChartProps {
  columns: IVerticalColumn[];
  /**
   * Plot box in pixels. Required: P2 draws recharts at an explicit size and never through
   * ResponsiveContainer, and a default width would silently squeeze the columns below 34px once a
   * caller passes more categories than it was picked for.
   */
  width: number;
  height: number;
  /** Formats the Y-axis ticks and the hover readout, which recharts generates rather than taking
   *  from the caller. */
  formatValue?: (value: number) => string;
  /**
   * Formats the Y-axis ticks and the accessible name. Defaults to `formatValue`; pass it separately
   * when the hover readout carries decoration that would be wrong on a generated tick.
   */
  formatAxisValue?: (value: number) => string;
  /**
   * Overrides the accessible name; without it the chart is named after its categories. Every
   * column's reading is stated up front rather than only on hover, since the glow below is a
   * mouse-only affordance that never reaches assistive technology.
   */
  ariaLabel?: string;
  /** Grouped mode: draws one bar per series in every column, from each column's `values`. */
  series?: IVerticalColumnSeries[];
  /** Column width in px; defaults to the 34 of `Group 33902`. */
  columnWidth?: number;
  columnRadius?: number;
  /** Gap between the bars of one group, in px. */
  groupGap?: number;
  /** Column fill; defaults to accent-purple. */
  color?: string;
  /** The hovered column's fill; defaults to `color`. */
  highlightColor?: string;
  /** The hovered column's glow as a drop-shadow triple; defaults to the CTA elevation. */
  highlightGlow?: string;
  /**
   * Opacity of the other columns while one is hovered; defaults to 0.32. Pass 1 where the resting
   * fill is already translucent, so it is not dimmed twice.
   */
  restOpacity?: number;
  hoverGuide?: IVerticalColumnHoverGuide;
  /** The hover readout in a screen's own palette. */
  tooltipTheme?: IChartTooltipTheme;
  /** Prints the reading above the bar(s) of this one column. */
  labelIndex?: number;
  /** Fixed Y ticks; the domain runs from the first to the last. */
  yTicks?: number[];
  /** Caption rotation in degrees; defaults to -35. Level captions are centred under the column. */
  tickAngle?: number;
  /** Labels every (n + 1)th column only, for dense time axes such as 24 hourly buckets. */
  xTickInterval?: number;
  xAxisHeight?: number;
  margin?: { top: number; right: number; bottom: number; left: number };
  axisStyle?: IVerticalColumnAxisStyle;
  className?: string;
}

/** Figma `Group 33902` draws the Opportunity Signal columns 34px wide. */
const COLUMN_WIDTH = 34;

const REST_OPACITY = 0.32;

const HIGHLIGHT_CLASS = 'vertical-column-chart-column-highlighted';

// The mockup paints a hovered column #4F46E5 with a purple glow and the rest #6976EB at 32% — a
// mouse-only affordance, not a permanent reading of which column leads. Neither fill is a token and
// neither may become one (PLA-8815 plan.md AF-3): both collapse onto accent-purple, the two states
// separate by opacity, and the glow reuses the CTA elevation.
// PERX3-REDESIGN round 5: one series, so it takes the ramp's brand stop rather than a position on
// a scale it has no other band to sit against. The two hover states stay an opacity split of the
// one fill, so the ramp moves both at once.
const COLUMN_FILL = seriesStop(2, tokens.color['accent-purple']);

const TOOLTIP_CONTENT_STYLE: React.CSSProperties = {
  padding: `${tokens.space['4']} ${tokens.space['8']}`,
  background: tokens.color['surface-card'],
  border: `1px solid ${tokens.color['border-subtle']}`,
  borderRadius: tokens.radius.sm,
  fontSize: tokens.type['label-size'],
  lineHeight: tokens.type['label-line']
};

const TOOLTIP_ITEM_STYLE: React.CSSProperties = { color: tokens.color['text-primary'] };

const TOOLTIP_LABEL_STYLE: React.CSSProperties = { color: tokens.color['text-muted'] };

const VALUE_LABEL_CLASS = 'vertical-column-chart-value-label';
const HIGHLIGHT_TICK_CLASS = 'vertical-column-chart-tick-highlighted';

interface IChartWrapperStyle {
  $glow?: string;
  $axis?: IVerticalColumnAxisStyle;
}

const ChartWrapper = styled.div<IChartWrapperStyle>`
  display: inline-flex;

  /* box-shadow does not paint on SVG geometry, and drop-shadow() takes the same
     offset/blur/colour triple the elevation token already carries. */
  .${HIGHLIGHT_CLASS} {
    filter: drop-shadow(${({ $glow }) => $glow || tokens.elevation.cta});
  }

  /* Axis text and rules are styled from here rather than through recharts' tick/axisLine props:
     those land as SVG presentation attributes, where var() support is not universal. */
  .recharts-cartesian-axis-tick-value {
    fill: ${({ $axis }) => $axis?.tickColor || tokens.color['text-muted']};
    font-size: ${({ $axis }) =>
      $axis?.tickFontSize === undefined ? tokens.type['label-size'] : scaledPx($axis.tickFontSize)};
    font-weight: ${tokens.type['body-weight']};
    ${({ $axis }) => ($axis?.tickFontFamily ? `font-family: ${$axis.tickFontFamily};` : '')}
  }

  .recharts-cartesian-axis-tick-value.${HIGHLIGHT_TICK_CLASS} {
    fill: ${({ $axis }) => $axis?.highlightTickColor || tokens.color['text-primary']};
    font-weight: ${tokens.type['card-title-weight']};
  }

  .${VALUE_LABEL_CLASS} {
    fill: ${({ $axis }) => $axis?.tickColor || tokens.color['text-muted']};
    font-size: ${({ $axis }) =>
      $axis?.tickFontSize === undefined ? tokens.type['label-size'] : scaledPx($axis.tickFontSize)};
    ${({ $axis }) => ($axis?.tickFontFamily ? `font-family: ${$axis.tickFontFamily};` : '')}
  }

  .recharts-cartesian-axis-line {
    stroke: ${({ $axis }) => $axis?.axisLineColor || tokens.color['border-subtle']};
  }
`;

const seriesKey = (index: number): string => `series${index}`;

/* The callout bubble is 24.26 tall and ends 7px above the guide's dot. */
const BUBBLE_OFFSET = 31.26;

// An angled tick's text runs down and to the LEFT of its own anchor point, so the leftmost column's
// label reaches past x=0 of the plot and gets clipped by the chart's own left edge without this.
// recharts keeps no margin by default — every other value here is 0, only left needs the room
// angled text asks for. The matching DOWNWARD reach (the same rotation sends the label past the
// chart's fixed total height, at its BOTTOM edge) is handled below via the XAxis `height`, not here:
// margin.bottom only adds blank space past the axis band, it doesn't grow the band a rotated label
// escapes out of.
const PLOT_MARGIN = { top: 0, right: 0, bottom: 0, left: 90 };

// A -35deg tick runs to roughly `textLength * sin(35deg)` below its own anchor point. Since the
// whole plot is drawn at a fixed total `height` (no scroll, no ResponsiveContainer), that reach is
// clipped at the SVG's own edge, not spilled onto the page — confirmed via the tick's rendered
// bounding box, not by eye: a real browser measurement (`getBoundingClientRect`) showed labels
// landing past the plot's own height even with every ancestor's CSS `overflow` reading `visible`,
// which jsdom cannot surface and a screenshot at the wrong crop can misread as still-broken. The
// longest label isn't this chart's OWN data — `HandoffNotEligibleBreakdown` feeds the same shared
// component its "not eligible" reasons ("Compliance / risk / fraud restriction", "Missing evidence /
// insufficient data"), which run longer than every "opportunity type" label this file's own fixture
// uses. 130 was measured against THAT caller's longest reason, not against Opportunity Signal's.
const XAXIS_HEIGHT = 130;

const plottableValue = (value: number): number => (Number.isFinite(value) && value > 0 ? value : 0);

const VerticalColumnChart: React.FC<IVerticalColumnChartProps> = ({
  columns,
  width,
  height,
  formatValue = formatNumber,
  formatAxisValue,
  ariaLabel,
  series,
  columnWidth = COLUMN_WIDTH,
  columnRadius,
  groupGap,
  color = COLUMN_FILL,
  highlightColor,
  highlightGlow,
  restOpacity = REST_OPACITY,
  hoverGuide,
  tooltipTheme,
  labelIndex,
  yTicks,
  tickAngle = -35,
  xTickInterval = 0,
  xAxisHeight = XAXIS_HEIGHT,
  margin = PLOT_MARGIN,
  axisStyle,
  className
}) => {
  const { t } = useMultiLanguageTranslation();
  // The bar under the pointer: its column, and its series in grouped mode.
  const [hovered, setHovered] = useState<{ index: number; series: number } | null>(null);

  const formatTick = formatAxisValue ?? formatValue;
  const grouped = Boolean(series?.length);

  const plotted = (columns ?? []).map(column => {
    const base = { ...column, value: plottableValue(column.value) };

    // Grouped rows carry one key per series; the single-series row is exactly what it always was.
    return grouped
      ? (series ?? []).reduce(
          (row, unused, seriesIndex) => ({
            ...row,
            [seriesKey(seriesIndex)]: plottableValue(column.values?.[seriesIndex] ?? 0)
          }),
          base
        )
      : base;
  });
  const total = plotted.reduce((sum, column) => sum + column.value, 0);

  // Axes around an empty plot area read as a broken card, so the card shows its own state block
  // instead (FR § Data states) — the same rule DonutChart applies to an unmeasured ring.
  if (total <= 0) {
    return null;
  }

  // Every reading is stated up front rather than only on hover: the glow and the tooltip below are
  // a mouse-only affordance, and neither reaches assistive technology.
  const labels = plotted
    .filter(column => column.value > 0)
    .map(column => `${column.label} ${formatTick(column.value)}`)
    .join(', ');
  const chartLabel = ariaLabel || t(`${TCustomerInsights}.Chart.Columns`, { labels });

  const highlightedLabel = hovered ? plotted[hovered.index]?.label ?? null : null;

  const contentStyle = tooltipTheme
    ? {
        ...TOOLTIP_CONTENT_STYLE,
        background: tooltipTheme.background,
        border: `1px solid ${tooltipTheme.border}`
      }
    : TOOLTIP_CONTENT_STYLE;
  const labelStyle = tooltipTheme ? { color: tooltipTheme.label } : TOOLTIP_LABEL_STYLE;
  const itemStyle = tooltipTheme ? { color: tooltipTheme.text } : TOOLTIP_ITEM_STYLE;

  // A custom renderer rather than `formatter`/`contentStyle`: the default per-item row prefixes the
  // reading with the series `name`, which here is only ever the internal `value` dataKey and reads
  // as noise beside a header that already names the category.
  const renderTooltip = ({ active, payload }: { active?: boolean; payload?: any[] }) => {
    if (!active || !payload || !payload.length) {
      return null;
    }

    const point = payload[0].payload as IVerticalColumn;

    return (
      <div style={contentStyle} data-testid="vertical-column-chart-tooltip">
        <div style={labelStyle}>{point.label}</div>
        {grouped ? (
          (series ?? []).map((entry, seriesIndex) => (
            <div key={entry.name} style={itemStyle}>
              {`${entry.name}: ${formatValue(point.values?.[seriesIndex] ?? 0)}`}
            </div>
          ))
        ) : (
          <div style={itemStyle}>{formatValue(point.value)}</div>
        )}
      </div>
    );
  };

  const readingAt = (index: number, seriesIndex: number): number =>
    grouped ? columns[index]?.values?.[seriesIndex] ?? 0 : columns[index]?.value ?? 0;

  const guideColor = (seriesIndex: number): string =>
    hoverGuide?.color ||
    (grouped ? series?.[seriesIndex]?.color ?? color : highlightColor || color);

  /* The guide ends on the centre of the hovered bar. recharts centres a category's bars in its band,
     so the centre follows from the band width, the bar width and the gap between grouped bars. */
  const renderGuide = ({ x1 = 0, x2 = 0, y1 = 0 }: { x1?: number; x2?: number; y1?: number }) => {
    if (!hoverGuide || !hovered) {
      return <g />;
    }

    const guide = {
      index: hovered.index,
      seriesIndex: hovered.series,
      color: guideColor(hovered.series),
      bubble: hoverGuide.bubble
    };

    const bars = grouped ? series?.length ?? 1 : 1;
    const gap = groupGap ?? 0;
    const band = (x2 - x1) / plotted.length;
    const group = bars * columnWidth + (bars - 1) * gap;
    const centre =
      x1 +
      band * (guide.index + 0.5) -
      group / 2 +
      (guide.seriesIndex ?? 0) * (columnWidth + gap) +
      columnWidth / 2;

    return (
      <g data-testid="vertical-column-chart-guide">
        <line x1={x1} x2={centre} y1={y1} y2={y1} stroke={guide.color} strokeDasharray="4 4" />
        {guide.bubble ? (
          <>
            <circle cx={centre} cy={y1} r={4} fill={guide.color} />
            <rect
              x={centre - 38.5}
              y={y1 - BUBBLE_OFFSET}
              width={77}
              height={24.26}
              rx={10}
              fill={guide.bubble.fill}
              style={{ filter: 'drop-shadow(0 4px 10px rgba(0, 0, 0, 0.6))' }}
            />
            <text
              x={centre}
              y={y1 - BUBBLE_OFFSET + 17}
              textAnchor="middle"
              className={VALUE_LABEL_CLASS}
              style={{
                fill: guide.bubble.text,
                fontSize: guide.bubble.fontSize ? scaledPx(guide.bubble.fontSize) : undefined
              }}
              data-testid="vertical-column-chart-guide-value"
            >
              {formatValue(readingAt(guide.index, guide.seriesIndex ?? 0))}
            </text>
          </>
        ) : null}
      </g>
    );
  };

  const renderValueLabel =
    (seriesIndex: number) =>
    ({
      x = 0,
      y = 0,
      width: barWidth = 0,
      index
    }: {
      x?: number | string;
      y?: number | string;
      width?: number | string;
      index?: number;
    }) =>
      index === labelIndex ? (
        <text
          x={Number(x) + Number(barWidth) / 2}
          y={Number(y) - 6}
          textAnchor="middle"
          className={VALUE_LABEL_CLASS}
          data-testid="vertical-column-chart-value-label"
        >
          {formatValue(readingAt(index, seriesIndex))}
        </text>
      ) : null;

  // Only a styled highlight caption needs its own tick renderer; every other caller keeps recharts'.
  // It is mounted whether or not a column is hovered and places the caption through recharts' own
  // Text, so the hover changes a class only: swapping renderers moved every caption on hover.
  const renderTick = axisStyle?.highlightTickColor
    ? ({ payload, ...tickProps }: Omit<TextProps, 'ref'> & { payload: { value: string } }) => (
        <Text
          {...tickProps}
          className={`recharts-cartesian-axis-tick-value${
            payload.value === highlightedLabel ? ` ${HIGHLIGHT_TICK_CLASS}` : ''
          }`}
        >
          {payload.value}
        </Text>
      )
    : undefined;

  const guideValue = hoverGuide && hovered ? readingAt(hovered.index, hovered.series) : null;

  return (
    <ChartWrapper
      $glow={highlightGlow}
      $axis={axisStyle}
      className={className}
      role="img"
      aria-label={chartLabel}
      data-testid="vertical-column-chart"
    >
      <BarChart
        width={width}
        height={height}
        data={plotted}
        margin={margin}
        barGap={grouped ? groupGap : undefined}
      >
        {axisStyle?.gridColor ? (
          <CartesianGrid
            vertical={false}
            stroke={axisStyle.gridColor}
            strokeDasharray={axisStyle.gridDasharray}
          />
        ) : null}
        {/* `interval={0}` keeps every category labelled — never thin the axis down to "every Nth" to
            make room — but a plot this width divided by up to seven columns leaves far less room
            per tick than a label like "Growth / Cross-sell / Up-sell" needs horizontally. Angling
            the tick is the standard recharts fix: each label's footprint becomes mostly vertical,
            so neighbouring ticks stop overwriting each other regardless of column count or length. */}
        <XAxis
          dataKey="label"
          interval={xTickInterval}
          tickLine={false}
          angle={tickAngle}
          textAnchor={tickAngle === 0 ? 'middle' : 'end'}
          height={xAxisHeight}
          tick={renderTick}
        />
        <YAxis
          tickFormatter={formatTick}
          axisLine={false}
          tickLine={false}
          domain={yTicks?.length ? [yTicks[0], yTicks[yTicks.length - 1]] : undefined}
          ticks={yTicks}
          interval={yTicks ? 0 : undefined}
          width={axisStyle?.yAxisWidth}
        />
        {/* A bubbled guide is the readout itself; a tooltip beside it would repeat the reading. */}
        {hoverGuide?.bubble ? null : (
          <Tooltip cursor={false} content={renderTooltip} isAnimationActive={false} />
        )}
        {guideValue !== null ? <ReferenceLine y={guideValue} shape={renderGuide} /> : null}
        {grouped ? (
          (series ?? []).map((entry, seriesIndex) => (
            <Bar
              key={entry.name}
              dataKey={seriesKey(seriesIndex)}
              name={entry.name}
              fill={entry.color}
              barSize={columnWidth}
              radius={columnRadius}
              isAnimationActive={false}
              onMouseEnter={(unused, index: number) => setHovered({ index, series: seriesIndex })}
              onMouseLeave={() => setHovered(null)}
            >
              {labelIndex !== undefined ? (
                <LabelList
                  dataKey={seriesKey(seriesIndex)}
                  content={renderValueLabel(seriesIndex)}
                />
              ) : null}
            </Bar>
          ))
        ) : (
          <Bar
            dataKey="value"
            barSize={columnWidth}
            radius={columnRadius}
            isAnimationActive={false}
            onMouseEnter={(unused, index: number) => setHovered({ index, series: 0 })}
            onMouseLeave={() => setHovered(null)}
          >
            {plotted.map(column => {
              const isHighlighted = column.label === highlightedLabel;

              return (
                // recharts ships no categorical palette (every column defaults to grey) and
                // outlines its shapes in white, which reads as a hairline crack on the dark card.
                <Cell
                  key={column.label}
                  fill={isHighlighted && highlightColor ? highlightColor : color}
                  stroke="none"
                  opacity={highlightedLabel !== null && !isHighlighted ? restOpacity : 1}
                  className={isHighlighted ? HIGHLIGHT_CLASS : undefined}
                />
              );
            })}
            {labelIndex !== undefined ? (
              <LabelList dataKey="value" content={renderValueLabel(0)} />
            ) : null}
          </Bar>
        )}
      </BarChart>
    </ChartWrapper>
  );
};

export default VerticalColumnChart;
